import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Question, BookOpen, ArrowSquareOut, FilePdf } from '@phosphor-icons/react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from './ui/sheet';
import { API } from '../lib/api';
import { allArticles, docForPath } from '../docs';
import { Block } from '../docs/Block';
import { printArticlePdf } from '../lib/docExport';

// Context-aware Help: a header button that opens a slide-in panel with the current module's guide.
export const HelpButton = () => {
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const articles = useMemo(() => allArticles(API), []);
  const docId = docForPath(loc.pathname);
  const article = docId ? articles.find((a) => a.id === docId) : null;

  // No dedicated guide for this route (e.g. Docs page itself, Profile) - hide the button.
  if (!article || loc.pathname === '/docs') return null;

  const openFull = () => { setOpen(false); nav(`/docs?doc=${article.id}`); };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="module-help-button" aria-label="Help & documentation for this module"
          className="flex items-center gap-1.5 border border-slate-200 bg-white hover:border-orange-400 hover:text-orange-600 rounded-md px-2.5 py-1.5 text-sm text-slate-600 transition-colors">
          <Question size={17} weight="bold" /> <span className="hidden sm:inline">Help</span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="module-help-panel" aria-describedby={undefined}>
        <div className="pe-8">
          <p className="font-mono-label text-[10px] uppercase text-orange-600 flex items-center gap-1"><BookOpen size={13} /> Module guide</p>
          <SheetTitle className="font-display text-xl font-extrabold tracking-tight mt-1" data-testid="module-help-title">{article.title}</SheetTitle>
          <p className="text-sm text-slate-500 mt-1">{article.summary}</p>
          <div className="flex gap-2 mt-3 mb-2 flex-wrap">
            <button data-testid="module-help-open-full" onClick={openFull}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors">
              <ArrowSquareOut size={14} /> Open full guide
            </button>
            <button data-testid="module-help-pdf" onClick={() => printArticlePdf(article)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-orange-400 hover:text-orange-600 transition-colors">
              <FilePdf size={14} /> Download PDF
            </button>
          </div>
          <div className="border-t border-slate-100 pt-2">
            {article.blocks.map((b, i) => <Block key={i} b={b} />)}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
